export interface SuccessResult<T> {
  _tag: 'success'
  data: T
}

export interface ErrorResult<T> {
  _tag: 'failure'
  error: T
}

export type Result<A, E> = SuccessResult<A> | ErrorResult<E>

const isSuccessResult = <T, E>(result: Result<T, E>): result is SuccessResult<T> => {
  return result._tag === 'success'
}

const isErrorResult = <T, E>(result: Result<T, E>): result is ErrorResult<E> => {
  return result._tag === 'failure'
}

const successValue = <T>(value: T): SuccessResult<T> => {
  return {
    _tag: 'success',
    data: value
  }
}

const errorValue = <E>(error: E): ErrorResult<E> => {
  return {
    _tag: 'failure',
    error
  }
}

const getError = <E>(errorResult: ErrorResult<E>) => {
  return errorResult.error
}

const getValue = <T>(successResult: SuccessResult<T>) => {
  return successResult.data
}

const fold = <T, E, R>(
  result: Result<T, E>,
  resolvers: {
    onSuccess: (value: T) => R
    onError: (error: E) => R
  }
) => {
  if (isSuccessResult(result)) {
    return resolvers.onSuccess(result.data)
  }

  return resolvers.onError(result.error)
}

interface ResultType {
  success: <T>(value: T) => SuccessResult<T>
  error: <E>(error: E) => ErrorResult<E>
  isSuccess: <T, E>(result: Result<T, E>) => result is SuccessResult<T>
  isError: <T, E>(result: Result<T, E>) => result is ErrorResult<E>
  getError: <E>(errorResult: ErrorResult<E>) => E
  getValue: <T>(successResult: SuccessResult<T>) => T
  fold: <T, E, R>(
    result: Result<T, E>,
    resolvers: {
      onSuccess: (value: T) => R
      onError: (error: E) => R
    }
  ) => R
}

export const result: ResultType = {
  success: successValue,
  error: errorValue,
  isSuccess: isSuccessResult,
  isError: isErrorResult,
  getError,
  getValue,
  fold
}
